import React, { useState } from 'react';
import { Button, TextField, MenuItem, Alert } from '@mui/material';
import { PAGE_BUILDER_ENDPOINTS } from '../../core/constants';
import { httpMiddleware, showSuccessToast, encodeToBase64 } from '../../core/Utils';
import createComponentJson from '../../core/component-list';
import { getComponentFromUniqueNameAndThemeId } from '../../core/facto-components';
import { htmlPageLayouts, buildHtmlPageLayout } from '../../html-page-layouts';
import { htmlPageErrorMessage } from '../../core/html-page-errors';

export function AddHtmlPageComponent({ selectedWebsite, selectedPageTemplate, onCreated, handleCloseOverlay, replace = false }) {
  const [name, setName] = useState('');
  const [basePath, setBasePath] = useState('');
  const [msid, setMsid] = useState('');
  const [layoutId, setLayoutId] = useState('blank');
  const [zip, setZip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault(); setError('');
    if (!zip || !zip.name.toLowerCase().endsWith('.zip')) { setError('Select an HTML ZIP file.'); return; }
    if (zip.size > 50 * 1024 * 1024) { setError('ZIP must be 50 MB or smaller.'); return; }
    setBusy(true);
    try {
      const form = new FormData(); form.append('zip', zip);
      let endpoint = PAGE_BUILDER_ENDPOINTS.HTML_PAGE_API;
      if (replace) {
        if (!selectedPageTemplate?.pageId) throw new Error('Select a HTML page first.');
        endpoint += '/' + selectedPageTemplate.pageId + '/bundle';
      } else {
        const themeId = Number(selectedPageTemplate?.themeId || localStorage.getItem('themeId'));
        if (!themeId || !selectedWebsite?.websiteId || !selectedWebsite?.clientId) throw new Error('Select a channel and theme first.');
        const selectedLayout = htmlPageLayouts.find((layout) => layout.id === layoutId);
        if (!selectedLayout) throw new Error('Invalid page layout.');
        let nodes = [];
        if (selectedLayout.includeChrome !== false) {
          const definitions = await createComponentJson();
          const chrome = definitions.filter((node) => /Header|Main Menu|Footer/.test(node.name) && !/Amp/.test(node.name)
            && getComponentFromUniqueNameAndThemeId(node.name, themeId));
          // One channel header and footer, using that theme's configured component defaults.
          const header = chrome.find((node) => /Header/.test(node.name));
          const footer = chrome.find((node) => /Footer/.test(node.name));
          if (!header || !footer) throw new Error('This theme requires a configured header and footer before creating HTML pages.');
          nodes = [header, footer].map((node) => ({ ...node, type: node.name }));
        }
        const layout = buildHtmlPageLayout(layoutId, nodes);
        form.append('metadata', new Blob([JSON.stringify({ clientId: selectedWebsite.clientId, websiteId: selectedWebsite.websiteId,
          themeId, name: name.trim(), basePath: basePath.trim().replace(/\/$/, ''), layoutId,
          publishstoryMSID: msid.trim() || null, ...layout, json: encodeToBase64(layout.json) })], { type: 'application/json' }));
      }
      const response = await httpMiddleware({ url: endpoint,
        method: 'POST', data: form, headers: { 'Content-Type': undefined }, withCredentials: true, timeout: 600000, rejectOnError: true });
      if (!response || response.status !== 200) throw new Error(response?.data?.code || 'Upload failed.');
      showSuccessToast(replace ? 'Static page ZIP replaced and published.' : 'Static page published.');
      onCreated(response.data, response.data.name); handleCloseOverlay();
    } catch (failure) { setError(htmlPageErrorMessage(failure.response?.data?.code || failure.message) || 'Unable to publish HTML page.'); }
    finally { setBusy(false); }
  };
  const title = replace ? 'Replace HTML page' : 'Create HTML page';
  const description = replace
    ? 'Upload a new ZIP to replace the current static page bundle.'
    : 'Upload a static page bundle and choose how it should be placed in your site.';

  return (
    <form className="html-page-form" onSubmit={submit}>
      <header className="html-page-form__header">
        <div>
          <p className="html-page-form__eyebrow">STATIC PAGE</p>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <button className="html-page-form__close" type="button" onClick={handleCloseOverlay} aria-label="Close">
          ×
        </button>
      </header>

      <div className="html-page-form__body">
        {!replace && (
          <div className="html-page-form__fields">
            <TextField
              required
              fullWidth
              label="Page name"
              placeholder="e.g. Campus ambassador"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ pattern: '[A-Za-z0-9_-]{1,80}', maxLength: 80 }}
            />
            <TextField
              required
              fullWidth
              label="Page URL"
              placeholder="/campus-ambassador"
              value={basePath}
              onChange={(e) => setBasePath(e.target.value)}
            />
            <TextField className="html-page-msid-field" label="Publishstory_MSID (optional)" value={msid} onChange={(e) => setMsid(e.target.value)} />
            <TextField fullWidth select label="Page layout" value={layoutId} onChange={(e) => setLayoutId(e.target.value)}>
              {htmlPageLayouts.map((layout) => <MenuItem key={layout.id} value={layout.id}>{layout.label}</MenuItem>)}
            </TextField>
          </div>
        )}

        <label className={`html-page-upload ${zip ? 'html-page-upload--selected' : ''}`} htmlFor="html-page-zip">
          <span className="html-page-upload__icon">↑</span>
          <span className="html-page-upload__content">
            <strong>{zip ? zip.name : 'Choose an HTML ZIP file'}</strong>
            <small>{zip ? `${(zip.size / (1024 * 1024)).toFixed(2)} MB selected` : 'Drop it here or browse from your computer'}</small>
          </span>
          <span className="html-page-upload__button">Browse</span>
          <input id="html-page-zip" type="file" accept=".zip,application/zip" disabled={busy} onChange={(e) => setZip(e.target.files[0] || null)} />
        </label>
        <p className="html-page-form__hint">Include an HTML file. If <code>index.html</code> is missing, another <code>.html</code> file is used as the entry page. Maximum ZIP size: 50 MB.</p>
        {error && <Alert severity="error">{error}</Alert>}
      </div>

      <footer className="html-page-form__footer">
        <Button className="html-page-form__cancel" disabled={busy} onClick={handleCloseOverlay}>Cancel</Button>
        <Button className="html-page-form__submit" type="submit" variant="contained" disabled={busy}>
          {busy ? 'Publishing…' : replace ? 'Replace page' : 'Publish HTML page'}
        </Button>
      </footer>
    </form>
  );
}
